import { createService } from "./createService"
import { SERVICE_INSTANCES } from "./serviceInstances"

const service = createService(SERVICE_INSTANCES.documents)

export const DOCUMENTS_INSTANCE = service.instanceKey

export const DOCUMENTS_ENDPOINTS = {
  SELECT: "/admin/documents/select",
  DELETE: "/admin/documents/delete",
  GET_TEMPLATE: "/admin/documents/gettemplate",
  CHECK_TEMPLATE: "/admin/documents/checktemplate",
  VIEW_TEMPLATES: "/admin/documents/view/templates",
  RESTORE_TEMPLATE: "/admin/documents/restore",
  REMOVE_TEMPLATE: "/admin/documents/remove",
  RENAME_TEMPLATE: "/admin/documents/rename",
  ASSIGN_TEMPLATE: "/admin/documents/assign",
  RECALL_TEMPLATE: "/admin/documents/recall",
  ADD: "/admin/documents/add",
  ADD_DOCUMENT: "/admin/documents/add_document",
  TEMPLATE_EMPLOYEES: "/admin/documents/templateemployees",
  UNASSIGNED_TEMPLATE_EMPLOYEES: "/admin/documents/unassignedtemplateemployees",
  GET_OFFER_LETTERS: "/admin/offer_letters/get",
  OL_CHECK: "/admin/offer_letters/checktemplate",
  OL_SAVE: "/admin/offer_letters/save",
  OL_ADD: "/admin/offer_letters/add",
  OL_FINISH: "/admin/offer_letters/finish",
  SELECT_OFFERS: "/admin/offers/select",
  FINISH_OFFER: "/admin/offers/finish",
  DELETE_OFFER: "/admin/offers/delete",
  EMPLOYEE_SELECT: "/employee/documents/select",
  EMPLOYEE_SIGN: "/employee/documents/sign",
  EMPLOYEE_ACKNOWLEDGE: "/employee/documents/acknowledge",
} as const

export const documentsService = {
  instanceKey: service.instanceKey,
  selectDocuments: (params?: {
    typeId: string
    searchParams?: string
    pageNumber?: number
    pageSize?: number
  }) =>
    service.get(`${DOCUMENTS_ENDPOINTS.SELECT}`, {
      typeId: params?.typeId ?? "",
      searchParams: params?.searchParams ?? "",
      pageNumber: String(params?.pageNumber ?? 1),
      pageSize: String(params?.pageSize ?? 1000),
    }),
  deleteDocument: (id: string) =>
    service.post(`${DOCUMENTS_ENDPOINTS.DELETE}?document_template_id=${id}`),
  getTemplate: (id: string) =>
    service.get(DOCUMENTS_ENDPOINTS.GET_TEMPLATE, { id }),
  checkDocTemplate: (description: string) =>
    service.get(
      `${DOCUMENTS_ENDPOINTS.CHECK_TEMPLATE}?description=${encodeURIComponent(
        description
      )}`
    ),
  viewDocumentTemplates: (id: string, x: string) =>
    service.get(DOCUMENTS_ENDPOINTS.VIEW_TEMPLATES, { id, x }),
  getTemplateEmployees: (
    documentTemplateId: string | number,
    params?: {
      searchParams?: string
      pageNumber?: number
      pageSize?: number
      personStatusId?: string | number
      departmentId?: string | number
      isAssigned?: boolean
      isAcknowledged?: boolean
      [key: string]: any
    }
  ) =>
    service.get(DOCUMENTS_ENDPOINTS.TEMPLATE_EMPLOYEES, {
      documentTemplateId,
      ...params,
    }),
  getUnassignedTemplateEmployees: (documentTemplateId: string | number) =>
    service.get(DOCUMENTS_ENDPOINTS.UNASSIGNED_TEMPLATE_EMPLOYEES, {
      documentTemplateId,
    }),
  restoreTemplate: (id: string) =>
    service.post(
      `${DOCUMENTS_ENDPOINTS.RESTORE_TEMPLATE}?document_template_id=${id}`
    ),
  removeTemplate: (id: string) =>
    service.post(
      `${DOCUMENTS_ENDPOINTS.REMOVE_TEMPLATE}?document_template_id=${id}`
    ),
  renameTemplate: (id: string, newName: string) =>
    service.post(
      `${DOCUMENTS_ENDPOINTS.RENAME_TEMPLATE}?document_template_id=${id}`,
      { newName }
    ),
  assignTemplate: (id: string, body: { person_id: string }[]) =>
    service.post(`${DOCUMENTS_ENDPOINTS.ASSIGN_TEMPLATE}?id=${id}`, body),
  recallTemplate: (id: string, actionType: string) =>
    service.post(
      `${DOCUMENTS_ENDPOINTS.RECALL_TEMPLATE}?document_template_id=${id}&actionType=${actionType}`
    ),
  addDocument: (formData: FormData) =>
    service.post(DOCUMENTS_ENDPOINTS.ADD, formData, {
      "Content-Type": "multipart/form-data",
    }),
  addDocumentFile: (formData: FormData) =>
    service.post(DOCUMENTS_ENDPOINTS.ADD_DOCUMENT, formData, {
      "Content-Type": "multipart/form-data",
    }),
  checkOfferLetterTemplate: (description: string, templateId: string) =>
    service.get(DOCUMENTS_ENDPOINTS.OL_CHECK, {
      description,
      document_template_id: templateId,
    }),
  saveOfferLetterTemplate: (body: Record<string, any>) =>
    service.post(DOCUMENTS_ENDPOINTS.OL_SAVE, body),
  addOfferLetterTemplate: (body: Record<string, any>) =>
    service.post(DOCUMENTS_ENDPOINTS.OL_ADD, body),
  finishOfferLetterTemplate: (body: Record<string, any>) =>
    service.post(DOCUMENTS_ENDPOINTS.OL_FINISH, body),
  getOfferLetters: (id?: string | number) =>
    service.get(DOCUMENTS_ENDPOINTS.GET_OFFER_LETTERS, { id }),
  selectOffers: (params?: {
    statusId?: string
    pageNumber?: number
    pageSize?: number
    searchParams?: string
  }) => service.get(DOCUMENTS_ENDPOINTS.SELECT_OFFERS, params),
  finishOffer: (body: Record<string, any>) =>
    service.post(DOCUMENTS_ENDPOINTS.FINISH_OFFER, body),
  deleteOffer: (id: string) =>
    service.post(`${DOCUMENTS_ENDPOINTS.DELETE_OFFER}?id=${id}`),
  selectEmployeeDocuments: () =>
    service.get(DOCUMENTS_ENDPOINTS.EMPLOYEE_SELECT),
  signEmployeeDocument: (body: Record<string, any>) =>
    service.post(DOCUMENTS_ENDPOINTS.EMPLOYEE_SIGN, body),
  acknowledgeEmployeeDocument: (body: Record<string, any>) =>
    service.post(DOCUMENTS_ENDPOINTS.EMPLOYEE_ACKNOWLEDGE, body),
  resendOffer: (id: string) => service.post(`/admin/offers/resend?id=${id}`),
  viewOffer: (key: string) => service.get(`/admin/offers/view`, { id: key }),
  acceptOffer: (body: Record<string, any>, accessToken?: string) =>
    service.post(
      `/offer/accept`,
      body,
      accessToken ? { access_token: accessToken } : undefined
    ),
  previewOffer: (key: string) => service.get(`/offer/preview`, { id: key }),
  addOffer: (body: Record<string, any>) =>
    service.post(`/admin/offers/add`, body),
  updateOffer: (id: string | number, body: Record<string, any>) =>
    service.post(`/admin/offers/update?id=${id}`, body),
  viewNewOffer: (key: string) =>
    service.get(`/admin/offers/view/new`, { id: key, x: key }),
  viewOfferLetter: (id: string | number) =>
    service.get(`/admin/offer_letters/view/details`, { id }),
  viewOfferByCode: (userId: string, code: string) =>
    service.get(`/offer/view/index`, { uId: userId, code }),
  getOfferLetterContent: (templateId: string | number) =>
    service.get(`/admin/offer_letters/getcontent`, {
      document_template_id: templateId,
    }),
  saveOfferContent: (offerId: string, content: string) =>
    service.post(`/admin/offers/save_content`, { offer_id: offerId, content }),
}
